import {Button, Space, Table, Tag} from "antd";
import {ColumnsType} from "antd/es/table";
import moment from "moment";
import {useNavigate} from "react-router-dom";
import {StyledDescriptionText} from "../../../../components/calls/upcoming-calls/upcoming-calls.style";
import {useGetDiscountCodesContext} from "../context";
import {DiscountCodesTableMenuOptions} from "./menu-options";

interface TableDataType {
  key: number;
  id: number;
  code: string;
  customersUsed: number;
  expiryDate: string;
  offeringCount: number;
  percentageOff: number;
  status: string;
  isAllOfferings: boolean;
}

export function DiscountCodesTableV2() {
  const {
    state: {
      discountCodes,
      isLoading,
      isFetching,
      currentPage,
      isPreviousData,
      discountCodesMeta,
    },
    dispatch,
  } = useGetDiscountCodesContext();

  const navigate = useNavigate();

  const discountCodesTableData: TableDataType[] = discountCodes.map((discount) => ({
    code: discount.code,
    customersUsed: discount.distinctCustomersUsed ?? discount.usage_count ?? 0,
    expiryDate: discount.end_date,
    id: discount.id,
    isAllOfferings: discount.isAllOfferings ?? false,
    key: discount.id,
    offeringCount: discount.offeringCount ?? discount.offering_names.length,
    percentageOff: discount.percentage_off,
    status: discount.statusName,
  }));

  const columns: ColumnsType<TableDataType> = [
    {
      title: "Discount code",
      dataIndex: "code",
      key: "code",
      width: "150px",
      render: (value) => <StyledDescriptionText>{value}</StyledDescriptionText>,
    },
    {
      title: "Value",
      dataIndex: "percentageOff",
      key: "percentageOff",
      width: "150px",
      render: (value) => <StyledDescriptionText>{`${value}% off`}</StyledDescriptionText>,
    },
    {
      title: "Customers",
      dataIndex: "customersUsed",
      key: "customersUsed",
      width: "130px",
      render: (value) => <StyledDescriptionText>{value}</StyledDescriptionText>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: "150px",
      render: (value: string) => (
        <Tag color={value.toLowerCase() === "active" ? "green" : "default"}>{value}</Tag>
      ),
    },
    {
      title: "Expiry date",
      dataIndex: "expiryDate",
      key: "expiryDate",
      width: "160px",
      render: (value) => (
        <StyledDescriptionText>
          {moment(value).format("ddd, MMM DD YYYY")}
        </StyledDescriptionText>
      ),
    },
    {
      title: "",
      key: "action",
      width: "90px",
      render: (_, record) => (
        <div onClick={(e) => e.stopPropagation()}>
          <DiscountCodesTableMenuOptions
            code={record.code}
            codeId={record.id}
            page={currentPage}
          />
        </div>
      ),
    },
  ];

  return (
    <div>
      <Space direction="vertical" size={20} style={{display: "flex"}}>
        <Table
          columns={columns}
          dataSource={discountCodesTableData}
          scroll={{x: 100}}
          pagination={false}
          loading={isLoading || isFetching}
          onRow={(record) => ({
            onClick: () => navigate(`/discounts/${record.id}/view`),
            style: {cursor: "pointer"},
          })}
        />
        <div>
          <Space size={12}>
            <Button
              size="small"
              onClick={() =>
                dispatch({
                  payload: Math.max(currentPage - 1, 1),
                  type: "update-current-page",
                })
              }
              disabled={currentPage === 1}
            >
              Previous
            </Button>
            <Button
              size="small"
              onClick={() => {
                if (!isPreviousData && Number(discountCodesMeta?.next_page) !== 0) {
                  dispatch({
                    payload: currentPage + 1,
                    type: "update-current-page",
                  });
                }
              }}
              disabled={
                isPreviousData || Number(discountCodesMeta?.next_page) === 0 || isFetching
              }
            >
              Next
            </Button>
          </Space>
        </div>
      </Space>
    </div>
  );
}
